import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Podcast, Search, LogOut, Settings, UserCircle, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LazyNotificationCenter = lazy(() => import("@/components/notifications/NotificationCenter").then(m => ({ default: m.NotificationCenter })));
const LazyGlobalSearch = lazy(() => import("@/components/layout/GlobalSearch").then(m => ({ default: m.GlobalSearch })));

const navLinks = [
  { label: "Features", href: "/#features" },
  { label: "How It Works", href: "/#how-it-works" },
  { label: "Pricing", href: "/#pricing" },
  { label: "Book A Studio", href: "/studios" },
  { label: "News", href: "/blog" },
];

interface MarketingHeaderProps {
  hideAuthButtons?: boolean;
  forceOpaque?: boolean;
  onLoginClick?: () => void;
  onSignUpClick?: () => void;
}

export function MarketingHeader({ hideAuthButtons = false, forceOpaque = false, onLoginClick, onSignUpClick }: MarketingHeaderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, signOut } = useAuth();

  const rafId = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      if (rafId.current) return;
      rafId.current = requestAnimationFrame(() => {
        setIsScrolled(window.scrollY > 50);
        rafId.current = 0;
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, []);

  const handleNavClick = (href: string) => {
    if (!href.includes('#')) {
      navigate(href);
      setIsOpen(false);
      return;
    }
    const hash = href.replace('/', '');
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) element.scrollIntoView({ behavior: "smooth" });
      }, 300);
    } else {
      const element = document.querySelector(hash);
      if (element) element.scrollIntoView({ behavior: "smooth" });
    }
    setIsOpen(false);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/auth");
    } catch (e) {
      const { toast } = await import("@/hooks/use-toast");
      toast({ title: "Sign out failed", description: "Please try again.", variant: "destructive" });
    }
  };

  const getInitials = () => {
    if (profile?.display_name) {
      return profile.display_name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
    }
    if (user?.email) return user.email[0].toUpperCase();
    return "U";
  };

  const getDisplayName = () => {
    return profile?.display_name || user?.email?.split("@")[0] || "User";
  };

  const isOnHomepage = location.pathname === '/';
  const isSignedIn = !!user;

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-accent focus:text-accent-foreground focus:rounded-md focus:text-sm focus:font-medium"
      >
        Skip to content
      </a>
      <header
        className={`fixed top-0 left-0 right-0 z-50 border-b transition-all duration-300 ${
          isScrolled || forceOpaque
            ? "bg-primary/95 backdrop-blur-md border-border/20 shadow-lg shadow-black/20"
            : "bg-transparent border-transparent shadow-none"
        }`}
      >
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex h-16 md:h-20 items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-accent flex items-center justify-center">
                <Podcast className="h-4 w-4 md:h-5 md:w-5 text-accent-foreground" aria-hidden="true" />
              </div>
              <span className="text-xl md:text-2xl font-bold text-secondary">PODGO</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation">
            {!isOnHomepage && (
              <Link to="/" className="text-secondary/80 hover:text-secondary transition-colors font-medium">
                Home
              </Link>
            )}
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link.href)}
                className="text-secondary/80 hover:text-secondary transition-colors font-medium inline-flex items-center gap-1.5"
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Desktop CTAs / User Actions */}
          {!hideAuthButtons && (
            <div className="hidden md:flex items-center gap-2">
              {isSignedIn ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-secondary/80 hover:text-secondary hover:bg-secondary/10"
                    onClick={() => navigate("/dashboard")}
                  >
                    <LayoutDashboard className="h-4 w-4 mr-1.5" />
                    Dashboard
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-secondary/80 hover:text-secondary hover:bg-secondary/10"
                    onClick={() => setSearchOpen(true)}
                    aria-label="Search"
                  >
                    <Search className="h-5 w-5" />
                  </Button>
                  <Suspense fallback={null}>
                    <div className="text-secondary [&_button]:text-secondary/80 [&_button:hover]:text-secondary [&_button:hover]:bg-secondary/10">
                      <LazyNotificationCenter />
                    </div>
                  </Suspense>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="relative h-9 w-9 rounded-full hover:bg-secondary/10">
                        <Avatar className="h-9 w-9 ring-2 ring-secondary/20">
                          {profile?.avatar_url && <AvatarImage src={profile.avatar_url} alt={getDisplayName()} />}
                          <AvatarFallback className="bg-accent text-accent-foreground text-sm">
                            {getInitials()}
                          </AvatarFallback>
                        </Avatar>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel>
                        <div className="flex flex-col">
                          <span className="font-medium">{getDisplayName()}</span>
                          <span className="text-xs text-muted-foreground">{user?.email}</span>
                        </div>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate("/settings")}>
                        <UserCircle className="mr-2 h-4 w-4" />
                        Profile
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/settings")}>
                        <Settings className="mr-2 h-4 w-4" />
                        Account Settings
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleSignOut} className="text-destructive focus:text-destructive">
                        <LogOut className="mr-2 h-4 w-4" />
                        Sign out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    className="text-secondary/80 hover:text-secondary hover:bg-secondary/10"
                    onClick={() => onLoginClick ? onLoginClick() : navigate("/auth")}
                  >
                    Log In
                  </Button>
                  <Button
                    className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold"
                    onClick={() => onSignUpClick ? onSignUpClick() : navigate("/auth?tab=signup")}
                  >
                    Get Started Free
                  </Button>
                </>
              )}
            </div>
          )}

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" className="text-secondary" aria-label="Open menu">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-primary border-border/10 w-[280px]">
              <div className="flex flex-col gap-6 mt-8">
                {/* Signed-in user info in mobile */}
                {isSignedIn && (
                  <div className="flex items-center gap-3 pb-4 border-b border-border/10">
                    <Avatar className="h-10 w-10 ring-2 ring-secondary/20">
                      {profile?.avatar_url && <AvatarImage src={profile.avatar_url} alt={getDisplayName()} />}
                      <AvatarFallback className="bg-accent text-accent-foreground text-sm">
                        {getInitials()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col">
                      <span className="text-secondary font-medium text-sm">{getDisplayName()}</span>
                      <span className="text-secondary/60 text-xs">{user?.email}</span>
                    </div>
                  </div>
                )}

                <nav className="flex flex-col gap-4" aria-label="Mobile navigation">
                  {!isOnHomepage && (
                    <Link to="/" onClick={() => setIsOpen(false)} className="text-secondary/80 hover:text-secondary transition-colors font-medium text-left text-lg">
                      Home
                    </Link>
                  )}
                  {isSignedIn && (
                    <button
                      onClick={() => { setIsOpen(false); navigate("/dashboard"); }}
                      className="text-secondary/80 hover:text-secondary transition-colors font-medium text-left text-lg inline-flex items-center gap-2"
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      Dashboard
                    </button>
                  )}
                  {navLinks.map((link) => (
                    <button
                      key={link.href}
                      onClick={() => handleNavClick(link.href)}
                      className="text-secondary/80 hover:text-secondary transition-colors font-medium text-left text-lg inline-flex items-center gap-2"
                    >
                      {link.label}
                    </button>
                  ))}
                </nav>

                {!hideAuthButtons && (
                  <div className="flex flex-col gap-3 pt-4 border-t border-border/10">
                    {isSignedIn ? (
                      <>
                        <Button
                          variant="ghost"
                          className="text-secondary/80 hover:text-secondary hover:bg-secondary/10 justify-start"
                          onClick={() => { setIsOpen(false); navigate("/settings"); }}
                        >
                          <UserCircle className="mr-2 h-4 w-4" />
                          Profile
                        </Button>
                        <Button
                          variant="ghost"
                          className="text-secondary/80 hover:text-secondary hover:bg-secondary/10 justify-start"
                          onClick={() => { setIsOpen(false); navigate("/settings"); }}
                        >
                          <Settings className="mr-2 h-4 w-4" />
                          Account Settings
                        </Button>
                        <Button
                          variant="ghost"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10 justify-start"
                          onClick={async () => {
                            setIsOpen(false);
                            try { await signOut(); } catch {}
                            window.location.href = "/auth";
                          }}
                        >
                          <LogOut className="mr-2 h-4 w-4" />
                          Sign out
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="ghost"
                          className="text-secondary/80 hover:text-secondary hover:bg-secondary/10 justify-start"
                          onClick={() => { setIsOpen(false); onLoginClick ? onLoginClick() : navigate("/auth"); }}
                        >
                          Log In
                        </Button>
                        <Button
                          className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold"
                          onClick={() => { setIsOpen(false); onSignUpClick ? onSignUpClick() : navigate("/auth?tab=signup"); }}
                        >
                          Get Started Free
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>

          {isSignedIn && (
            <Suspense fallback={null}>
              <LazyGlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
            </Suspense>
          )}
        </div>
      </div>
      </header>
    </>
  );
}
